"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.measures = exports.viewDeclarations = exports.eventsObservationsView = exports.scoresListableCountViewV2 = exports.scoresListableCountView = exports.scoresCategoricalViewV2 = exports.scoresBooleanViewV2 = exports.scoresNumericViewV2 = exports.scoresCategoricalView = exports.scoresBooleanView = exports.scoresNumericView = exports.observationsView = exports.eventsTracesView = exports.traceView = void 0;
exports.getViewDeclaration = getViewDeclaration;
exports.requiresV2 = requiresV2;
exports.getResultUnit = getResultUnit;
const zod_1 = require("zod");
const types_1 = require("./types");
const errors_1 = require("../../errors");
const eventsTable_1 = require("../../eventsTable");
const scores_1 = require("../../domain/scores");
const evalExecutionMetadata_1 = require("../evals/evalExecutionMetadata");
// The data model defines all available dimensions, measures, and the timeDimension for a given view.
// Make sure to update web/src/features/dashboard/lib/dashboardUiTableToViewMapping.ts if you make changes
exports.traceView = {
    name: "traces",
    description: "Traces represent a group of observations and typically represent a single request or operation.",
    dimensions: {
        id: {
            sql: "traces.id",
            alias: "id",
            type: "string",
            description: "Unique identifier of the trace.",
        },
        name: {
            sql: "traces.name",
            alias: "name",
            type: "string",
            description: "Name assigned to the trace (often the endpoint or operation).",
        },
        tags: {
            sql: "traces.tags",
            alias: "tags",
            type: "string[]",
            description: "User-defined tags associated with the trace.",
        },
        userId: {
            sql: "traces.user_id",
            alias: "userId",
            type: "string",
            description: "Identifier of the user triggering the trace.",
        },
        sessionId: {
            sql: "traces.session_id",
            alias: "sessionId",
            type: "string",
            description: "Identifier of the session triggering the trace.",
        },
        release: {
            sql: "traces.release",
            alias: "release",
            type: "string",
            description: "Release version of the trace.",
        },
        version: {
            sql: "traces.version",
            alias: "version",
            type: "string",
            description: "Version of the trace.",
        },
        environment: {
            sql: "traces.environment",
            alias: "environment",
            type: "string",
            description: "Deployment environment (e.g., production, staging).",
        },
        timestampMonth: {
            sql: "formatDateTime(traces.timestamp, '%Y-%m')",
            alias: "timestampMonth",
            type: "string",
            description: "Month of the trace timestamp in YYYY-MM format.",
        },
    },
    measures: {
        count: {
            sql: "count(*)",
            alias: "count",
            type: "integer",
            description: "Total number of traces.",
            unit: "traces",
        },
        observationsCount: {
            sql: "uniq(observations.id)",
            alias: "observationsCount",
            type: "integer",
            relationTable: "observations",
            description: "Unique observations linked to the trace.",
            unit: "observations",
            defaultAggregation: "sum",
        },
        scoresCount: {
            sql: "uniq(scores.id)",
            alias: "scoresCount",
            type: "integer",
            relationTable: "scores",
            description: "Unique scores attached to the trace.",
            unit: "scores",
            defaultAggregation: "sum",
        },
        uniqueUserIds: {
            sql: "uniq(traces.user_id)",
            alias: "uniqueUserIds",
            type: "integer",
            description: "Count of unique userIds.",
            unit: "users",
        },
        uniqueSessionIds: {
            sql: "uniq(traces.session_id)",
            alias: "uniqueSessionIds",
            type: "integer",
            description: "Count of unique sessionIds.",
            unit: "sessions",
        },
        latency: {
            sql: "date_diff('millisecond', min(observations.start_time), max(observations.end_time))",
            alias: "latency",
            type: "integer",
            relationTable: "observations",
            description: "Elapsed time between the first and last observation inside the trace.",
            unit: "millisecond",
            defaultAggregation: "avg",
        },
        totalTokens: {
            sql: "sumMap(observations.usage_details)['total']",
            alias: "totalTokens",
            type: "integer",
            relationTable: "observations",
            description: "Sum of tokens consumed by all observations in the trace.",
            unit: "tokens",
            defaultAggregation: "sum",
        },
        totalCost: {
            sql: "sum(observations.total_cost)",
            alias: "totalCost",
            type: "decimal",
            relationTable: "observations",
            description: "Total cost accumulated across observations in the trace.",
            unit: "USD",
            defaultAggregation: "sum",
        },
    },
    tableRelations: {
        observations: {
            name: "observations",
            joinConditionSql: "ON traces.id = observations.trace_id AND traces.project_id = observations.project_id",
            timeDimension: "start_time",
        },
        scores: {
            name: "scores",
            joinConditionSql: "ON traces.id = scores.trace_id AND traces.project_id = scores.project_id",
            timeDimension: "timestamp",
        },
    },
    segments: [],
    timeDimension: "timestamp",
    baseCte: `traces FINAL`,
};
exports.eventsTracesView = {
    name: "events_traces",
    description: "Traces built from events table aggregation - mirrors v1 traces view with 100% API compatibility.",
    dimensions: {
        // This is an internal compatibility declaration for persisted v1 trace
        // widgets. Keep id/userId/sessionId cardinality metadata aligned with v1:
        // enabling V4 must not invalidate existing time-series breakdowns. New
        // traces widgets remain unavailable through the public V4 widget path. Do
        // not add highCardinality here: v1 does not set it for these dimensions.
        id: {
            sql: "events_traces.trace_id",
            alias: "id",
            type: "string",
            description: "Unique identifier of the trace.",
            // This is the GROUP BY identity column
        },
        name: {
            sql: (0, eventsTable_1.eventsTableTraceNameSqlForAlias)("events_traces"),
            alias: "name",
            type: "string",
            description: "Name assigned to the trace (often the endpoint or operation).",
            // First try most-recent non-empty trace_name, then fall back to root event's name
            aggregationFunction: (0, eventsTable_1.eventsTableTraceNameAggregationSqlForAlias)("events_traces"),
            // Pruning columns for WHERE: OR'd together to help ClickHouse skip blocks,
            // then dimension.sql is AND'd for exact row-level match.
            filterSql: {
                where: ["events_traces.trace_name", "events_traces.name"],
            },
        },
        tags: {
            sql: "events_traces.tags",
            alias: "tags",
            type: "string[]",
            description: "User-defined tags associated with the trace.",
            aggregationFunction: "arrayDistinct(flatten(groupArray(events_traces.tags)))",
        },
        userId: {
            sql: "nullIf(events_traces.user_id, '')",
            alias: "userId",
            type: "string",
            description: "Identifier of the user triggering the trace.",
            aggregationFunction: "argMaxIf(events_traces.user_id, events_traces.event_ts, events_traces.user_id <> '')",
        },
        sessionId: {
            sql: "nullIf(events_traces.session_id, '')",
            alias: "sessionId",
            type: "string",
            description: "Identifier of the session triggering the trace.",
            aggregationFunction: "argMaxIf(events_traces.session_id, events_traces.event_ts, events_traces.session_id <> '')",
        },
        release: {
            sql: "nullIf(events_traces.release, '')",
            alias: "release",
            type: "string",
            description: "Release version of the trace.",
            aggregationFunction: "argMaxIf(events_traces.release, events_traces.event_ts, events_traces.release <> '')",
        },
        version: {
            sql: "nullIf(events_traces.version, '')",
            alias: "version",
            type: "string",
            description: "Version of the trace.",
            aggregationFunction: "argMaxIf(events_traces.version, events_traces.event_ts, events_traces.version <> '')",
        },
        environment: {
            sql: "nullIf(events_traces.environment, '')",
            alias: "environment",
            type: "string",
            description: "Deployment environment (e.g., production, staging).",
            aggregationFunction: "argMaxIf(events_traces.environment, events_traces.event_ts, events_traces.environment <> '')",
        },
        timestampMonth: {
            sql: "events_traces.start_time",
            alias: "timestampMonth",
            type: "string",
            description: "Month of the trace timestamp in YYYY-MM format.",
            aggregationFunction: "formatDateTime(min(events_traces.start_time), '%Y-%m')",
        },
    },
    measures: {
        count: {
            sql: "uniq(events_traces.trace_id)",
            alias: "count",
            type: "integer",
            description: "Total number of traces.",
            unit: "traces",
        },
        observationsCount: {
            sql: "uniqIf(events_traces.span_id, events_traces.parent_span_id != '')",
            alias: "observationsCount",
            type: "integer",
            description: "Unique observations linked to the trace.",
            unit: "observations",
            defaultAggregation: "sum",
        },
        scoresCount: {
            sql: "uniq(scores.id)",
            alias: "scoresCount",
            type: "integer",
            relationTable: "scores",
            description: "Unique scores attached to the trace.",
            unit: "scores",
            defaultAggregation: "sum",
        },
        uniqueUserIds: {
            // Keep this numeric base aligned with v1: persisted widgets may use any
            // numeric aggregation. The count/uniq overrides below preserve distinct
            // identifier semantics over the denormalized events table.
            sql: "uniq(nullIf(events_traces.user_id, ''))",
            alias: "uniqueUserIds",
            type: "integer",
            description: "Count of unique userIds.",
            unit: "users",
            aggregationOverrides: {
                // Legacy widgets called this aggregation `count`, but its intended
                // result is distinct users. Remap only the generated query to `uniq`
                // while preserving the persisted aggregation and `count_*` alias.
                count: {
                    sql: "argMaxIf(nullIf(events_traces.user_id, ''), events_traces.event_ts, events_traces.user_id <> '')",
                    type: "string",
                    queryAggregation: "uniq",
                },
                uniq: {
                    sql: "argMaxIf(nullIf(events_traces.user_id, ''), events_traces.event_ts, events_traces.user_id <> '')",
                    type: "string",
                },
            },
        },
        uniqueSessionIds: {
            sql: "uniq(nullIf(events_traces.session_id, ''))",
            alias: "uniqueSessionIds",
            type: "integer",
            description: "Count of unique sessionIds.",
            unit: "sessions",
            aggregationOverrides: {
                // See uniqueUserIds: keep the legacy `count` contract while executing
                // the aggregation with distinct-value semantics.
                count: {
                    sql: "argMaxIf(nullIf(events_traces.session_id, ''), events_traces.event_ts, events_traces.session_id <> '')",
                    type: "string",
                    queryAggregation: "uniq",
                },
                uniq: {
                    sql: "argMaxIf(nullIf(events_traces.session_id, ''), events_traces.event_ts, events_traces.session_id <> '')",
                    type: "string",
                },
            },
        },
        latency: {
            sql: "date_diff('millisecond', minIf(events_traces.start_time, events_traces.parent_span_id != ''), maxIf(events_traces.end_time, events_traces.parent_span_id != ''))",
            alias: "latency",
            type: "integer",
            description: "Elapsed time between the first and last observation inside the trace.",
            unit: "millisecond",
            defaultAggregation: "avg",
        },
        totalTokens: {
            sql: "sumMapIf(events_traces.usage_details, events_traces.parent_span_id != '')['total']",
            alias: "totalTokens",
            type: "integer",
            description: "Sum of tokens consumed by all observations in the trace.",
            unit: "tokens",
            defaultAggregation: "sum",
        },
        totalCost: {
            sql: "sumIf(toNullable(events_traces.total_cost), events_traces.parent_span_id != '')",
            alias: "totalCost",
            type: "decimal",
            description: "Total cost accumulated across observations in the trace.",
            unit: "USD",
            defaultAggregation: "sum",
        },
    },
    tableRelations: {
        scores: {
            name: "scores",
            joinConditionSql: "ON events_traces.trace_id = scores.trace_id AND events_traces.project_id = scores.project_id",
            timeDimension: "timestamp",
        },
    },
    segments: [],
    timeDimension: "start_time",
    // Trace aggregation can use either semantic-root representation without
    // changing result cardinality, so app roots remain useful here.
    rootEventCondition: {
        column: "trace_id",
        condition: (0, eventsTable_1.eventsTableIsRootObservationSqlForAlias)("events_traces"),
    },
    baseCte: `events_core events_traces`,
};
const createEvaluatorDimensions = (tableAlias) => ({
    evaluatorId: {
        sql: `coalesce(nullIf(${tableAlias}.evaluator_id, ''), ${tableAlias}.evaluation_rule_id)`,
        alias: "evaluatorId",
        type: "string",
        description: "Identifier of the evaluator, falling back to its legacy evaluation rule identifier.",
        highCardinality: true,
        uiHidden: true,
    },
    isEvaluatorTest: {
        sql: `${tableAlias}.evaluator_execution_is_test`,
        alias: "isEvaluatorTest",
        type: "boolean",
        description: "Whether this row belongs to an evaluator test run.",
        uiHidden: true,
    },
});
exports.observationsView = {
    name: "observations",
    description: "Observations represent individual requests or operations within a trace, such as Spans, Generations, Events, and agent-specific types like Agents and Tools.",
    dimensions: {
        id: {
            sql: "observations.id",
            alias: "id",
            type: "string",
            description: "Unique identifier for the observation.",
        },
        traceId: {
            sql: "observations.trace_id",
            alias: "traceId",
            type: "string",
            description: "Identifier linking the observation to its parent trace.",
        },
        traceName: {
            sql: "traces.name",
            alias: "traceName",
            type: "string",
            relationTable: "traces",
            description: "Name of the parent trace.",
        },
        environment: {
            sql: "observations.environment",
            alias: "environment",
            type: "string",
            description: "Deployment environment (e.g., production, staging).",
        },
        parentObservationId: {
            sql: "observations.parent_observation_id",
            alias: "parentObservationId",
            type: "string",
            description: "Identifier of the parent observation. Empty for the root span.",
        },
        type: {
            sql: "observations.type",
            alias: "type",
            type: "string",
            description: "Type of the observation, e.g. SPAN, GENERATION, EVENT, AGENT, TOOL, CHAIN, RETRIEVER, EVALUATOR, EMBEDDING, or GUARDRAIL.",
        },
        name: {
            sql: "observations.name",
            alias: "name",
            type: "string",
            description: "Name of the observation.",
        },
        level: {
            sql: "observations.level",
            alias: "level",
            type: "string",
            description: "Logging level of the observation.",
        },
        version: {
            sql: "observations.version",
            alias: "version",
            type: "string",
            description: "Version of the observation.",
        },
        tags: {
            sql: "traces.tags",
            alias: "tags",
            type: "string[]",
            relationTable: "traces",
            description: "User-defined tags associated with the trace.",
        },
        providedModelName: {
            sql: "observations.provided_model_name",
            alias: "providedModelName",
            type: "string",
            description: "Name of the model used for the observation.",
        },
        promptName: {
            sql: "observations.prompt_name",
            alias: "promptName",
            type: "string",
            description: "Name of the prompt used for the observation.",
        },
        promptVersion: {
            sql: "observations.prompt_version",
            alias: "promptVersion",
            type: "number",
            description: "Version of the prompt used for the observation.",
        },
        userId: {
            sql: "traces.user_id",
            alias: "userId",
            type: "string",
            relationTable: "traces",
            description: "Identifier of the user triggering the observation.",
        },
        sessionId: {
            sql: "traces.session_id",
            alias: "sessionId",
            type: "string",
            relationTable: "traces",
            description: "Identifier of the session triggering the observation.",
        },
        traceRelease: {
            sql: "traces.release",
            alias: "traceRelease",
            type: "string",
            relationTable: "traces",
            description: "Release version of the parent trace.",
        },
        traceVersion: {
            sql: "traces.version",
            alias: "traceVersion",
            type: "string",
            relationTable: "traces",
            description: "Version of the parent trace.",
        },
        startTimeMonth: {
            sql: "formatDateTime(observations.start_time, '%Y-%m')",
            alias: "startTimeMonth",
            type: "string",
            description: "Month of the observation start_time in YYYY-MM format.",
        },
        toolNames: {
            sql: "mapKeys(observations.tool_definitions)",
            alias: "toolNames",
            type: "arrayString",
            description: "Names of available tools defined for the observation.",
            explodeArray: true,
        },
        calledToolNames: {
            sql: "arrayDistinct(observations.tool_call_names)",
            alias: "calledToolNames",
            type: "arrayString",
            description: "Names of tools that were called by the observation.",
            explodeArray: true,
        },
    },
    measures: {
        count: {
            sql: "@@AGG@@(1)",
            aggs: { agg: "count" },
            alias: "count",
            type: "integer",
            description: "Total number of observations.",
            unit: "observations",
        },
        latency: {
            sql: "date_diff('millisecond', @@AGG1@@(observations.start_time), @@AGG1@@(observations.end_time))",
            aggs: { agg1: "any" },
            alias: "latency",
            type: "integer",
            description: "Latency of an individual observation (start time to end time).",
            unit: "millisecond",
            defaultAggregation: "avg",
        },
        streamingLatency: {
            // Return NULL if `completion_start_time` is NULL to avoid misleading latency values
            sql: "if(isNull(@@AGG1@@(observations.completion_start_time)), CAST(NULL AS Nullable(Int64)), date_diff('millisecond', @@AGG1@@(observations.completion_start_time), @@AGG1@@(observations.end_time)))",
            aggs: { agg1: "any" },
            alias: "streamingLatency",
            type: "integer",
            description: "Latency of the generation step (completion start time to end time).",
            unit: "millisecond",
            defaultAggregation: "avg",
        },
        inputTokens: {
            sql: "arraySum(mapValues(mapFilter(x -> positionCaseInsensitive(x.1, 'input') > 0, @@AGG1@@(usage_details))))",
            aggs: { agg1: "any" },
            alias: "inputTokens",
            type: "integer",
            description: "Sum of input tokens consumed by the observation.",
            unit: "tokens",
            defaultAggregation: "sum",
        },
        outputTokens: {
            sql: "arraySum(mapValues(mapFilter(x -> positionCaseInsensitive(x.1, 'output') > 0, @@AGG1@@(usage_details))))",
            aggs: { agg1: "any" },
            alias: "outputTokens",
            type: "integer",
            description: "Sum of output tokens produced by the observation.",
            unit: "tokens",
            defaultAggregation: "sum",
        },
        totalTokens: {
            sql: "@@AGG1@@(usage_details)['total']",
            aggs: { agg1: "sumMap" },
            alias: "totalTokens",
            type: "integer",
            description: "Sum of tokens consumed by the observation.",
            unit: "tokens",
            defaultAggregation: "sum",
        },
        outputTokensPerSecond: {
            // Calculate average output tokens per second. Denominator uses seconds to align
            // with the `tokens/s` unit; NULL values avoided by guarding against a 0-second
            // duration.
            sql: "arraySum(mapValues(mapFilter(x -> positionCaseInsensitive(x.1, 'output') > 0, @@AGG1@@(usage_details)))) / nullIf(date_diff('second', @@AGG1@@(observations.completion_start_time), @@AGG1@@(observations.end_time)), 0)",
            aggs: { agg1: "any" },
            alias: "outputTokensPerSecond",
            type: "decimal",
            description: "Average number of output tokens produced per second between completion start time and span end time.",
            unit: "tokens/s",
            defaultAggregation: "avg",
        },
        tokensPerSecond: {
            sql: "@@AGG1@@(usage_details)['total'] / nullIf(date_diff('second', @@AGG2@@(observations.start_time), @@AGG2@@(observations.end_time)), 0)",
            aggs: { agg1: "sumMap", agg2: "any" },
            alias: "tokensPerSecond",
            type: "decimal",
            description: "Average number of tokens consumed per second by the observation.",
            unit: "tokens/s",
            defaultAggregation: "avg",
        },
        inputCost: {
            sql: "arraySum(mapValues(mapFilter(x -> positionCaseInsensitive(x.1, 'input') > 0, @@AGG1@@(cost_details))))",
            aggs: { agg1: "any" },
            alias: "inputCost",
            type: "decimal",
            description: "Sum of input cost incurred by the observation.",
            unit: "USD",
            defaultAggregation: "sum",
        },
        outputCost: {
            sql: "arraySum(mapValues(mapFilter(x -> positionCaseInsensitive(x.1, 'output') > 0, @@AGG1@@(cost_details))))",
            aggs: { agg1: "any" },
            alias: "outputCost",
            type: "decimal",
            description: "Sum of output cost incurred by the observation.",
            unit: "USD",
            defaultAggregation: "sum",
        },
        totalCost: {
            sql: "@@AGG1@@(total_cost)",
            aggs: { agg1: "sum" },
            alias: "totalCost",
            type: "decimal",
            description: "Total cost incurred by the observation.",
            unit: "USD",
            defaultAggregation: "sum",
        },
        timeToFirstToken: {
            // Return NULL if `completion_start_time` is NULL to represent unknown TTFT
            sql: "if(isNull(@@AGG1@@(observations.completion_start_time)), CAST(NULL AS Nullable(Int64)), date_diff('millisecond', @@AGG1@@(observations.start_time), @@AGG1@@(observations.completion_start_time)))",
            aggs: { agg1: "any" },
            alias: "timeToFirstToken",
            type: "integer",
            description: "Time to first token for the observation.",
            unit: "millisecond",
            defaultAggregation: "avg",
        },
        countScores: {
            sql: "uniq(scores.id)",
            alias: "countScores",
            type: "integer",
            relationTable: "scores",
            description: "Unique scores attached to the observation.",
            unit: "scores",
            defaultAggregation: "sum",
        },
        toolDefinitions: {
            sql: "nullIf(length(mapKeys(@@AGG1@@(tool_definitions))), 0)",
            aggs: { agg1: "any" },
            alias: "toolDefinitions",
            type: "integer",
            description: "Number of available tools per observation.",
            unit: "tools",
            defaultAggregation: "avg",
        },
        toolCalls: {
            sql: "nullIf(length(@@AGG1@@(tool_calls)), 0)",
            aggs: { agg1: "any" },
            alias: "toolCalls",
            type: "integer",
            description: "Number of tool calls made by the observation, including parallel calls. Use the Sum aggregation to get the total number of tool calls.",
            unit: "calls",
            defaultAggregation: "sum",
        },
        toolCallInvocations: {
            sql: "countEqual(@@AGG1@@(observations.tool_call_names), calledToolNames)",
            aggs: { agg1: "any" },
            alias: "toolCallInvocations",
            type: "integer",
            requiresDimension: "calledToolNames",
            description: "Number of individual tool-call invocations, counting repeated calls to the same tool within one observation. Automatically grouped by the Called Tool Names dimension. Use the Sum aggregation for totals and rankings.",
            unit: "calls",
            defaultAggregation: "sum",
        },
    },
    tableRelations: {
        traces: {
            name: "traces",
            joinConditionSql: "ON observations.trace_id = traces.id AND observations.project_id = traces.project_id",
            timeDimension: "timestamp",
        },
        scores: {
            name: "scores",
            joinConditionSql: "ON observations.id = scores.observation_id AND observations.project_id = scores.project_id",
            timeDimension: "timestamp",
        },
    },
    segments: [],
    timeDimension: "start_time",
    baseCte: `observations FINAL`,
};
const scoreBaseDimensions = {
    traceName: {
        sql: "traces.name",
        alias: "traceName",
        type: "string",
        relationTable: "traces",
        description: "Name of the parent trace.",
    },
    tags: {
        sql: "traces.tags",
        alias: "tags",
        type: "string[]",
        relationTable: "traces",
        description: "User-defined tags associated with the trace.",
    },
    userId: {
        sql: "traces.user_id",
        alias: "userId",
        type: "string",
        relationTable: "traces",
        description: "Identifier of the user triggering the trace.",
    },
    sessionId: {
        sql: "traces.session_id",
        alias: "sessionId",
        type: "string",
        relationTable: "traces",
        description: "Identifier of the session triggering the trace.",
    },
    traceRelease: {
        sql: "traces.release",
        alias: "traceRelease",
        type: "string",
        relationTable: "traces",
        description: "Release version of the parent trace.",
    },
    traceVersion: {
        sql: "traces.version",
        alias: "traceVersion",
        type: "string",
        relationTable: "traces",
        description: "Version of the parent trace.",
    },
    observationName: {
        sql: "observations.name",
        alias: "observationName",
        type: "string",
        relationTable: "observations",
        description: "Name of the observation associated with the score.",
    },
    observationModelName: {
        sql: "observations.provided_model_name",
        alias: "observationModelName",
        type: "string",
        relationTable: "observations",
        description: "Name of the model used for the observation.",
    },
    observationPromptName: {
        sql: "observations.prompt_name",
        alias: "observationPromptName",
        type: "string",
        relationTable: "observations",
        description: "Name of the prompt used for the observation.",
    },
    observationPromptVersion: {
        sql: "observations.prompt_version",
        alias: "observationPromptVersion",
        type: "number",
        relationTable: "observations",
        description: "Version of the prompt used for the observation.",
    },
};
// v2 scores dimensions
const scoresV2BaseDimensions = {
    sessionId: {
        sql: "scores.session_id",
        alias: "sessionId",
        type: "string",
        description: "Identifier of the session.",
        highCardinality: true,
    },
    // Run-level scores (dataset_run_id on scores table). Used by experiment
    // widgets via entityDimension + filters.
    datasetRunId: {
        sql: "nullIf(scores.dataset_run_id, '')",
        alias: "datasetRunId",
        type: "string",
        description: "Identifier of the dataset run (experiment) for the score.",
        highCardinality: true,
        uiHidden: true,
    },
    // Trace metadata on events table (accessed via events_traces JOIN)
    traceName: {
        sql: (0, eventsTable_1.eventsTableTraceNameSqlForAlias)("events_traces"),
        alias: "traceName",
        type: "string",
        relationTable: "events_traces",
        description: "Name of the parent trace.",
    },
    userId: {
        sql: "nullIf(events_traces.user_id, '')",
        alias: "userId",
        type: "string",
        relationTable: "events_traces",
        description: "Identifier of the user.",
        highCardinality: true,
    },
    tags: {
        sql: "events_traces.tags",
        alias: "tags",
        type: "string[]",
        relationTable: "events_traces",
        description: "User-defined tags.",
    },
    traceRelease: {
        sql: "nullIf(events_traces.release, '')",
        alias: "traceRelease",
        type: "string",
        relationTable: "events_traces",
        description: "Release version.",
    },
    traceVersion: {
        sql: "nullIf(events_traces.version, '')",
        alias: "traceVersion",
        type: "string",
        relationTable: "events_traces",
        description: "Version of the parent trace.",
    },
    // Observation fields from events table (accessed via events_observations JOIN)
    observationName: {
        sql: "events_observations.name",
        alias: "observationName",
        type: "string",
        relationTable: "events_observations",
        description: "Name of the observation associated with the score.",
    },
    observationModelName: {
        sql: "nullIf(events_observations.provided_model_name, '')",
        alias: "observationModelName",
        type: "string",
        relationTable: "events_observations",
        description: "Name of the model used for the observation.",
    },
    observationPromptName: {
        sql: "nullIf(events_observations.prompt_name, '')",
        alias: "observationPromptName",
        type: "string",
        relationTable: "events_observations",
        description: "Name of the prompt used for the observation.",
    },
    observationPromptVersion: {
        sql: "events_observations.prompt_version",
        alias: "observationPromptVersion",
        type: "number",
        relationTable: "events_observations",
        description: "Version of the prompt used for the observation.",
    },
    // Experiment fields for observation-attached scores. These stay hidden from
    // the custom dashboard builder for now and are used by internal experiment
    // widgets via entityDimension + filters.
    experimentName: {
        sql: "nullIf(events_observations.experiment_name, '')",
        alias: "experimentName",
        type: "string",
        relationTable: "events_observations",
        description: "Name of the experiment associated with the score.",
        highCardinality: true,
        uiHidden: true,
    },
    experimentId: {
        sql: "nullIf(events_observations.experiment_id, '')",
        alias: "experimentId",
        type: "string",
        relationTable: "events_observations",
        description: "ID of the experiment associated with the score.",
        highCardinality: true,
        uiHidden: true,
    },
};
// Factory for shared score-specific dimensions (both numeric and categorical)
const createScoreSpecificDimensions = (tableAlias, isV2 = false) => ({
    id: {
        sql: `${tableAlias}.id`,
        alias: "id",
        type: "string",
        description: "Unique identifier of the score entry.",
        ...(isV2 && { highCardinality: true }),
    },
    environment: {
        sql: `${tableAlias}.environment`,
        alias: "environment",
        type: "string",
        description: "Deployment environment (e.g., production, staging).",
    },
    name: {
        sql: `${tableAlias}.name`,
        alias: "name",
        type: "string",
        description: "Name of the score (e.g., accuracy, toxicity).",
    },
    evaluatorId: {
        sql: `${tableAlias}.evaluator_id`,
        alias: "evaluatorId",
        type: "string",
        description: "Identifier of the evaluator that produced the score.",
        highCardinality: true,
        uiHidden: true,
    },
    ruleId: {
        sql: `${tableAlias}.evaluation_rule_id`,
        alias: "ruleId",
        type: "string",
        description: "Identifier of the evaluation rule, including legacy job configurations.",
        highCardinality: true,
        uiHidden: true,
    },
    isEvaluatorTest: {
        sql: `toBool(${tableAlias}.metadata['${evalExecutionMetadata_1.EvalExecutionMetadataKey.EVALUATOR_TEST}'] = 'true')`,
        alias: "isEvaluatorTest",
        type: "boolean",
        description: "Whether this score belongs to an evaluator test run.",
        uiHidden: true,
    },
    source: {
        sql: `${tableAlias}.source`,
        alias: "source",
        type: "string",
        description: "Origin of the score. Can be API, ANNOTATION, or EVAL.",
    },
    dataType: {
        sql: `${tableAlias}.data_type`,
        alias: "dataType",
        type: "string",
        description: "Internal data type of the score (NUMERIC, BOOLEAN, CATEGORICAL).",
    },
    traceId: {
        sql: `${tableAlias}.trace_id`,
        alias: "traceId",
        type: "string",
        description: "Identifier of the parent trace.",
        ...(isV2 && { highCardinality: true }),
    },
    configId: {
        sql: `${tableAlias}.config_id`,
        alias: "configId",
        type: "string",
        description: "Identifier of the config associated with the score.",
    },
    timestampMonth: {
        sql: `formatDateTime(${tableAlias}.timestamp, '%Y-%m')`,
        alias: "timestampMonth",
        type: "string",
        description: "Month of the score timestamp in YYYY-MM format.",
    },
    timestampDay: {
        sql: `formatDateTime(${tableAlias}.timestamp, '%Y-%m-%d')`,
        alias: "timestampDay",
        type: "string",
        description: "Day of the score timestamp in YYYY-MM-DD format.",
    },
    observationId: {
        sql: `${tableAlias}.observation_id`,
        alias: "observationId",
        type: "string",
        description: "Identifier of the observation associated with the score.",
        ...(isV2 && { highCardinality: true }),
    },
});
// Shared table relations factory
const createScoreTableRelations = (version) => {
    if (version === "v1") {
        return {
            traces: {
                name: "traces",
                joinConditionSql: "ON scores.trace_id = traces.id AND scores.project_id = traces.project_id",
                timeDimension: "timestamp",
            },
            observations: {
                name: "observations",
                joinConditionSql: "ON scores.observation_id = observations.id AND scores.project_id = observations.project_id",
                timeDimension: "start_time",
            },
        };
    }
    return {
        events_traces: {
            name: "events_core",
            // Legacy and dual-write ingestion materialize a parentless synthetic
            // trace event and propagate trace metadata to it. Native V4 may not, but
            // its app roots come from newer SDKs and are expected to carry trace_name.
            // A semantic-root join can match both rows in dual-write data and
            // multiply scores for little fallback value.
            joinConditionSql: "ON scores.trace_id = events_traces.trace_id AND scores.project_id = events_traces.project_id AND events_traces.parent_span_id = ''",
            timeDimension: "start_time",
            useFinal: false,
        },
        events_observations: {
            name: "events_core",
            joinConditionSql: "ON scores.project_id = events_observations.project_id AND scores.trace_id = events_observations.trace_id AND scores.observation_id = events_observations.span_id",
            timeDimension: "start_time",
            useFinal: false,
        },
    };
};
function scoresNumericViewBase(version) {
    const baseDimensions = version === "v1" ? scoreBaseDimensions : scoresV2BaseDimensions;
    return {
        name: "scores_numeric",
        description: "Scores are flexible objects that are used for evaluations. This view contains numeric and boolean scores.",
        dimensions: {
            ...baseDimensions, // v1 keeps trace-JOIN dimensions
            ...createScoreSpecificDimensions("scores_numeric", version === "v2"),
            value: {
                sql: "scores_numeric.value",
                alias: "value",
                type: "number",
                description: "Value of the score.",
            },
        },
        measures: {
            count: {
                sql: "count(*)",
                alias: "count",
                type: "integer",
                description: "Total number of scores.",
                unit: "scores",
            },
            value: {
                sql: "any(value)",
                alias: "value",
                type: "number",
                description: "Value of the score.",
                defaultAggregation: "avg",
            },
        },
        tableRelations: createScoreTableRelations(version),
        segments: [
            {
                column: "data_type",
                // Positive allow-list: only NUMERIC and BOOLEAN count as numeric scores.
                // CATEGORICAL, TEXT, and CORRECTION are excluded.
                operator: "any of",
                value: ["NUMERIC", "BOOLEAN"],
                type: "stringOptions",
            },
        ], // Numeric
        timeDimension: "timestamp",
        baseCte: `scores scores_numeric FINAL`,
    };
}
function scoresCategoricalViewBase(version) {
    const baseDimensions = version === "v1" ? scoreBaseDimensions : scoresV2BaseDimensions;
    return {
        name: "scores_categorical",
        description: "Scores are flexible objects that are used for evaluations. This view contains categorical scores.",
        dimensions: {
            ...baseDimensions,
            ...createScoreSpecificDimensions("scores_categorical", version === "v2"),
            stringValue: {
                sql: "string_value",
                alias: "stringValue",
                type: "string",
                description: "Value of the score.",
            },
        },
        measures: {
            count: {
                sql: "count(*)",
                alias: "count",
                type: "integer",
                description: "Total number of scores.",
                unit: "scores",
            },
        },
        tableRelations: createScoreTableRelations(version),
        segments: [
            {
                column: "data_type",
                operator: "=",
                value: "CATEGORICAL",
                type: "string",
            },
        ], // Categorical
        timeDimension: "timestamp",
        baseCte: `scores scores_categorical FINAL`,
    };
}
function scoresBooleanViewBase(version) {
    const baseDimensions = version === "v1" ? scoreBaseDimensions : scoresV2BaseDimensions;
    return {
        name: "scores_boolean",
        description: "Scores are flexible objects that are used for evaluations. This view contains boolean scores.",
        dimensions: {
            ...baseDimensions,
            ...createScoreSpecificDimensions("scores_boolean", version === "v2"),
            booleanValue: {
                sql: "toBool(scores_boolean.value)",
                alias: "booleanValue",
                type: "boolean",
                description: "Boolean value of the score.",
            },
        },
        measures: {
            count: {
                sql: "count(*)",
                alias: "count",
                type: "integer",
                description: "Total number of scores.",
                unit: "scores",
            },
            value: {
                sql: "any(value)",
                alias: "value",
                type: "number",
                description: "Numeric 0/1 value of the score. Its average is the true-rate.",
                defaultAggregation: "avg",
            },
        },
        tableRelations: createScoreTableRelations(version),
        segments: [
            {
                column: "data_type",
                operator: "=",
                value: "BOOLEAN",
                type: "string",
            },
        ],
        timeDimension: "timestamp",
        baseCte: `scores scores_boolean FINAL`,
    };
}
exports.scoresNumericView = scoresNumericViewBase("v1");
exports.scoresBooleanView = scoresBooleanViewBase("v1");
exports.scoresCategoricalView = scoresCategoricalViewBase("v1");
// v2 Scores Views
exports.scoresNumericViewV2 = scoresNumericViewBase("v2");
exports.scoresBooleanViewV2 = scoresBooleanViewBase("v2");
exports.scoresCategoricalViewV2 = scoresCategoricalViewBase("v2");
/**
 * Count-only view scoped to every listable score type (see
 * `LISTABLE_SCORE_TYPES`), unlike `scores-numeric`/`scores-categorical`
 * which each cover only a subset. Powers `ScoresOutlierStrip`'s "Count"
 * mode; deliberately has no `value` measure, since categorical/text rows
 * carry no real numeric value — "Value" mode stays a separate query against
 * the unchanged `scores-numeric` view instead.
 */
function scoresListableCountViewBase(version) {
    const baseDimensions = version === "v1" ? scoreBaseDimensions : scoresV2BaseDimensions;
    return {
        name: "scores_listable_count",
        description: "Internal-only, count-only view scoping to every listable score type. Powers the scores overview strip's Count mode; not part of the public views enum.",
        dimensions: {
            ...baseDimensions,
            ...createScoreSpecificDimensions("scores_listable_count", version === "v2"),
        },
        measures: {
            count: {
                sql: "count(*)",
                alias: "count",
                type: "integer",
                description: "Total number of scores across every listable score type.",
                unit: "scores",
            },
        },
        tableRelations: createScoreTableRelations(version),
        segments: [
            {
                column: "data_type",
                operator: "any of",
                value: [...scores_1.LISTABLE_SCORE_TYPES],
                type: "stringOptions",
            },
        ],
        timeDimension: "timestamp",
        baseCte: `scores scores_listable_count FINAL`,
    };
}
exports.scoresListableCountView = scoresListableCountViewBase("v1");
exports.scoresListableCountViewV2 = scoresListableCountViewBase("v2");
// Events-Observations View - queries from events table instead of observations table
exports.eventsObservationsView = {
    name: "events_observations",
    description: "Observations represent individual requests or operations within a trace, such as Spans, Generations, Events, and agent-specific types like Agents and Tools.",
    dimensions: {
        id: {
            sql: "events_observations.span_id",
            alias: "id",
            type: "string",
            description: "Unique identifier for the observation.",
            highCardinality: true,
            uiHidden: true,
        },
        ...createEvaluatorDimensions("events_observations"),
        traceId: {
            sql: "events_observations.trace_id",
            alias: "traceId",
            type: "string",
            description: "Identifier linking the observation to its parent trace.",
            highCardinality: true,
            uiHidden: true,
        },
        environment: {
            sql: "nullIf(events_observations.environment, '')",
            alias: "environment",
            type: "string",
            description: "Deployment environment (e.g., production, staging).",
        },
        parentObservationId: {
            sql: "nullIf(events_observations.parent_span_id, '')",
            alias: "parentObservationId",
            type: "string",
            description: "Identifier of the parent observation. Empty for the root span.",
            highCardinality: true,
            uiHidden: true,
        },
        isRootObservation: {
            sql: `toBool(${(0, eventsTable_1.eventsTableIsRootObservationSqlForAlias)("events_observations")})`,
            alias: "isRootObservation",
            type: "boolean",
            description: "Whether the observation is a semantic root, including app roots with an external parent.",
            uiHidden: true,
        },
        type: {
            sql: "events_observations.type",
            alias: "type",
            type: "string",
            description: "Type of the observation, e.g. SPAN, GENERATION, EVENT, AGENT, TOOL, CHAIN, RETRIEVER, EVALUATOR, EMBEDDING, or GUARDRAIL.",
        },
        name: {
            sql: "events_observations.name",
            alias: "name",
            type: "string",
            description: "Name of the observation.",
        },
        level: {
            sql: "events_observations.level",
            alias: "level",
            type: "string",
            description: "Logging level of the observation.",
        },
        version: {
            sql: "nullIf(events_observations.version, '')",
            alias: "version",
            type: "string",
            description: "Version of the observation.",
        },
        // Denormalized trace fields from events table
        userId: {
            sql: "nullIf(events_observations.user_id, '')",
            alias: "userId",
            type: "string",
            description: "Identifier of the user triggering the observation.",
            highCardinality: true,
        },
        sessionId: {
            sql: "nullIf(events_observations.session_id, '')",
            alias: "sessionId",
            type: "string",
            description: "Identifier of the session triggering the observation.",
            highCardinality: true,
        },
        tags: {
            sql: "events_observations.tags",
            alias: "tags",
            type: "string[]",
            description: "User-defined tags associated with the trace.",
        },
        release: {
            sql: "nullIf(events_observations.release, '')",
            alias: "release",
            type: "string",
            description: "Release version.",
        },
        // Backwards-compatible field definitions (for API parity with v1)
        traceName: {
            sql: (0, eventsTable_1.eventsTableTraceNameSqlForAlias)("events_observations"),
            alias: "traceName",
            type: "string",
            description: "Name of the parent trace (backwards-compatible with v1).",
        },
        traceRelease: {
            sql: "nullIf(events_observations.release, '')",
            alias: "traceRelease",
            type: "string",
            description: "Release version of the parent trace (backwards-compatible with v1, maps to denormalized release field).",
        },
        traceVersion: {
            sql: "nullIf(events_observations.version, '')",
            alias: "traceVersion",
            type: "string",
            description: "Version of the parent trace (backwards-compatible with v1, maps to denormalized version field).",
        },
        providedModelName: {
            sql: "nullIf(events_observations.provided_model_name, '')",
            alias: "providedModelName",
            type: "string",
            description: "Name of the model used for the observation.",
        },
        promptName: {
            sql: "nullIf(events_observations.prompt_name, '')",
            alias: "promptName",
            type: "string",
            description: "Name of the prompt used for the observation.",
        },
        promptVersion: {
            sql: "events_observations.prompt_version",
            alias: "promptVersion",
            type: "number",
            description: "Version of the prompt used for the observation.",
        },
        startTimeMonth: {
            sql: "formatDateTime(events_observations.start_time, '%Y-%m')",
            alias: "startTimeMonth",
            type: "string",
            description: "Month of the observation start_time in YYYY-MM format.",
        },
        skillNamesAvailable: {
            sql: "arrayDistinct(events_observations.skills_available.skillName)",
            alias: "skillNamesAvailable",
            type: "arrayString",
            explodeArray: true,
            description: "Names of skills made available.",
        },
        skillsNamesLoaded: {
            sql: "arrayDistinct(events_observations.skills_resource_loaded.skillName)",
            alias: "skillsNamesLoaded",
            type: "arrayString",
            explodeArray: true,
            description: "Names of skills made available.",
        },
        skillIdsAvailable: {
            sql: "arrayDistinct(events_observations.skills_available.langfuseSkillId)",
            alias: "skillIdsAvailable",
            type: "arrayString",
            explodeArray: true,
            uiHidden: true,
            description: "Langfuse IDs of skill versions made available.",
        },
        skillIdsLoaded: {
            sql: "arrayDistinct(events_observations.skills_resource_loaded.langfuseSkillId)",
            alias: "skillIdsLoaded",
            type: "arrayString",
            explodeArray: true,
            uiHidden: true,
            description: "Langfuse IDs of skill versions with loaded files.",
        },
        loadedFilePaths: {
            sql: "arrayDistinct(events_observations.skills_resource_loaded.filePath)",
            alias: "loadedFilePaths",
            type: "arrayString",
            explodeArray: true,
            uiHidden: true,
            description: "Paths of loaded skill files.",
        },
        toolNames: {
            sql: "mapKeys(events_observations.tool_definitions)",
            alias: "toolNames",
            type: "arrayString",
            description: "Names of available tools defined for the observation.",
            explodeArray: true,
        },
        calledToolNames: {
            sql: "arrayDistinct(events_observations.tool_call_names)",
            alias: "calledToolNames",
            type: "arrayString",
            description: "Names of tools that were called by the observation.",
            explodeArray: true,
        },
        costType: {
            sql: "mapKeys(events_observations.cost_details)",
            alias: "costType",
            type: "string",
            description: "Cost category key from cost_details map (e.g. 'input', 'output', 'total').",
            pairExpand: {
                valuesSql: "mapValues(events_observations.cost_details)",
                valueAlias: "cost_value",
            },
        },
        usageType: {
            sql: "mapKeys(events_observations.usage_details)",
            alias: "usageType",
            type: "string",
            description: "Token usage category key from usage_details map (e.g. 'input', 'output', 'total').",
            pairExpand: {
                valuesSql: "mapValues(events_observations.usage_details)",
                valueAlias: "usage_value",
            },
        },
        // Experiment dimensions (v2 only - experiment data only exists in events table)
        experimentName: {
            sql: "nullIf(events_observations.experiment_name, '')",
            alias: "experimentName",
            type: "string",
            description: "Name of the experiment this observation belongs to.",
            highCardinality: true,
        },
        experimentDatasetId: {
            sql: "nullIf(events_observations.experiment_dataset_id, '')",
            alias: "experimentDatasetId",
            type: "string",
            description: "Dataset used in the experiment.",
            highCardinality: true,
        },
        experimentId: {
            sql: "nullIf(events_observations.experiment_id, '')",
            alias: "experimentId",
            type: "string",
            description: "ID of the experiment this observation belongs to.",
            highCardinality: true,
            uiHidden: true, // Only available as filter, not as breakdown dimension
        },
    },
    measures: {
        count: {
            sql: "@@AGG@@(1)",
            aggs: { agg: "count" },
            alias: "count",
            type: "integer",
            description: "Total number of observations.",
            unit: "observations",
        },
        traceId: {
            sql: "@@AGG@@(events_observations.trace_id)",
            aggs: { agg: "any" },
            alias: "traceId",
            type: "string",
            description: "Trace identifier; apply uniq aggregation to count distinct traces.",
        },
        uniqueUserIds: {
            sql: "@@AGG@@(nullIf(events_observations.user_id, ''))",
            aggs: { agg: "any" },
            alias: "uniqueUserIds",
            type: "string",
            description: "User identifier; apply uniq aggregation to count distinct users.",
            unit: "users",
        },
        uniqueSessionIds: {
            sql: "@@AGG@@(nullIf(events_observations.session_id, ''))",
            aggs: { agg: "any" },
            alias: "uniqueSessionIds",
            type: "string",
            description: "Session identifier; apply uniq aggregation to count distinct sessions.",
            unit: "sessions",
        },
        latency: {
            sql: "date_diff('millisecond', @@AGG1@@(events_observations.start_time), @@AGG1@@(events_observations.end_time))",
            aggs: { agg1: "any" },
            alias: "latency",
            type: "integer",
            description: "Latency of an individual observation (start time to end time).",
            unit: "millisecond",
            defaultAggregation: "avg",
        },
        streamingLatency: {
            sql: "if(isNull(@@AGG1@@(events_observations.completion_start_time)), CAST(NULL AS Nullable(Int64)), date_diff('millisecond', @@AGG1@@(events_observations.completion_start_time), @@AGG1@@(events_observations.end_time)))",
            aggs: { agg1: "any" },
            alias: "streamingLatency",
            type: "integer",
            description: "Latency of the generation step (completion start time to end time).",
            unit: "millisecond",
            defaultAggregation: "avg",
        },
        inputTokens: {
            sql: "arraySum(mapValues(mapFilter(x -> positionCaseInsensitive(x.1, 'input') > 0, @@AGG1@@(usage_details))))",
            aggs: { agg1: "any" },
            alias: "inputTokens",
            type: "integer",
            description: "Sum of input tokens consumed by the observation.",
            unit: "tokens",
            defaultAggregation: "sum",
        },
        outputTokens: {
            sql: "arraySum(mapValues(mapFilter(x -> positionCaseInsensitive(x.1, 'output') > 0, @@AGG1@@(usage_details))))",
            aggs: { agg1: "any" },
            alias: "outputTokens",
            type: "integer",
            description: "Sum of output tokens produced by the observation.",
            unit: "tokens",
            defaultAggregation: "sum",
        },
        totalTokens: {
            sql: "@@AGG1@@(usage_details)['total']",
            aggs: { agg1: "sumMap" },
            alias: "totalTokens",
            type: "integer",
            description: "Sum of tokens consumed by the observation.",
            unit: "tokens",
            defaultAggregation: "sum",
        },
        outputTokensPerSecond: {
            sql: "arraySum(mapValues(mapFilter(x -> positionCaseInsensitive(x.1, 'output') > 0, @@AGG1@@(usage_details)))) / nullIf(date_diff('second', @@AGG1@@(events_observations.completion_start_time), @@AGG1@@(events_observations.end_time)), 0)",
            aggs: { agg1: "any" },
            alias: "outputTokensPerSecond",
            type: "decimal",
            description: "Average number of output tokens produced per second between completion start time and span end time.",
            unit: "tokens/s",
            defaultAggregation: "avg",
        },
        tokensPerSecond: {
            sql: "@@AGG1@@(usage_details)['total'] / nullIf(date_diff('second', @@AGG2@@(events_observations.start_time), @@AGG2@@(events_observations.end_time)), 0)",
            aggs: { agg1: "sumMap", agg2: "any" },
            alias: "tokensPerSecond",
            type: "decimal",
            description: "Average number of tokens consumed per second by the observation.",
            unit: "tokens/s",
            defaultAggregation: "avg",
        },
        inputCost: {
            sql: "arraySum(mapValues(mapFilter(x -> positionCaseInsensitive(x.1, 'input') > 0, @@AGG1@@(cost_details))))",
            aggs: { agg1: "any" },
            alias: "inputCost",
            type: "decimal",
            description: "Sum of input cost incurred by the observation.",
            unit: "USD",
            defaultAggregation: "sum",
        },
        outputCost: {
            sql: "arraySum(mapValues(mapFilter(x -> positionCaseInsensitive(x.1, 'output') > 0, @@AGG1@@(cost_details))))",
            aggs: { agg1: "any" },
            alias: "outputCost",
            type: "decimal",
            description: "Sum of output cost incurred by the observation.",
            unit: "USD",
            defaultAggregation: "sum",
        },
        totalCost: {
            sql: "@@AGG1@@(toNullable(total_cost))",
            aggs: { agg1: "sum" },
            alias: "totalCost",
            type: "decimal",
            description: "Total cost incurred by the observation.",
            unit: "USD",
            defaultAggregation: "sum",
        },
        timeToFirstToken: {
            sql: "if(isNull(@@AGG1@@(events_observations.completion_start_time)), CAST(NULL AS Nullable(Int64)), date_diff('millisecond', @@AGG1@@(events_observations.start_time), @@AGG1@@(events_observations.completion_start_time)))",
            aggs: { agg1: "any" },
            alias: "timeToFirstToken",
            type: "integer",
            description: "Time to first token for the observation.",
            unit: "millisecond",
            defaultAggregation: "avg",
        },
        countScores: {
            sql: "uniq(scores.id)",
            alias: "countScores",
            type: "integer",
            relationTable: "scores",
            description: "Unique scores attached to the observation.",
            unit: "scores",
            defaultAggregation: "sum",
        },
        toolDefinitions: {
            sql: "nullIf(length(mapKeys(@@AGG1@@(events_observations.tool_definitions))), 0)",
            aggs: { agg1: "any" },
            alias: "toolDefinitions",
            type: "integer",
            description: "Number of available tools per observation.",
            unit: "tools",
            defaultAggregation: "avg",
        },
        toolCalls: {
            sql: "nullIf(length(@@AGG1@@(events_observations.tool_calls)), 0)",
            aggs: { agg1: "any" },
            alias: "toolCalls",
            type: "integer",
            description: "Number of tool calls made by the observation, including parallel calls. Use the Sum aggregation to get the total number of tool calls.",
            unit: "calls",
            defaultAggregation: "sum",
        },
        toolCallInvocations: {
            sql: "countEqual(@@AGG1@@(events_observations.tool_call_names), calledToolNames)",
            aggs: { agg1: "any" },
            alias: "toolCallInvocations",
            type: "integer",
            requiresDimension: "calledToolNames",
            description: "Number of individual tool-call invocations, counting repeated calls to the same tool within one observation. Automatically grouped by the Called Tool Names dimension. Use the Sum aggregation for totals and rankings.",
            unit: "calls",
            defaultAggregation: "sum",
        },
        costByType: {
            sql: "cost_value",
            alias: "costByType",
            type: "decimal",
            unit: "USD",
            defaultAggregation: "sum",
            requiresDimension: "costType",
            description: "Sum of cost per category. The costType dimension is auto-included to emit the ARRAY JOIN that brings cost_value into scope.",
        },
        usageByType: {
            sql: "usage_value",
            alias: "usageByType",
            type: "integer",
            unit: "tokens",
            defaultAggregation: "sum",
            requiresDimension: "usageType",
            description: "Sum of token usage per category. The usageType dimension is auto-included to emit the ARRAY JOIN that brings usage_value into scope.",
        },
    },
    tableRelations: {
        // No traces relation - userId, sessionId, tags are denormalized on events table
        scores: {
            name: "scores",
            joinConditionSql: "ON events_observations.span_id = scores.observation_id AND events_observations.project_id = scores.project_id",
            timeDimension: "timestamp",
        },
    },
    segments: [],
    timeDimension: "start_time",
    baseCte: "events_core events_observations", // No FINAL modifier needed for events_core table
};
// Versioned view declarations
exports.viewDeclarations = {
    v1: {
        traces: exports.traceView,
        observations: exports.observationsView, // Old: observations table
        "scores-numeric": exports.scoresNumericView,
        "scores-boolean": exports.scoresBooleanView,
        "scores-categorical": exports.scoresCategoricalView,
    },
    v2: {
        traces: exports.eventsTracesView,
        observations: exports.eventsObservationsView,
        "scores-numeric": exports.scoresNumericViewV2,
        "scores-boolean": exports.scoresBooleanViewV2,
        "scores-categorical": exports.scoresCategoricalViewV2,
    },
};
// Helper function for view resolution
const internalViewDeclarations = {
    v1: exports.scoresListableCountView,
    v2: exports.scoresListableCountViewV2,
};
function getViewDeclaration(
// Widened (not just `z.infer<typeof views>`) so the handful of
// server-only callers that know about `SCORES_LISTABLE_COUNT_VIEW` can
// resolve it too, without adding it to the public `views` enum.
viewName, version = "v1") {
    if (viewName === types_1.SCORES_LISTABLE_COUNT_VIEW) {
        return internalViewDeclarations[version];
    }
    const versionViews = exports.viewDeclarations[version];
    // TypeScript knows the exact shape of each version now
    if (!(viewName in versionViews)) {
        const supportedViews = Object.keys(versionViews).join(", ");
        throw new errors_1.InvalidRequestError(`View '${viewName}' is not supported in version '${version}'. ` +
            `Supported views for ${version}: ${supportedViews}`);
    }
    return versionViews[viewName];
}
/**
 * Check whether a widget's selected fields require v2 view declarations.
 * Returns true if any dimension or measure only exists in the v2 declaration
 * for the given view (e.g. pairExpand dimensions, requiresDimension measures).
 */
function requiresV2(params) {
    const v1View = exports.viewDeclarations.v1[params.view];
    const v2View = exports.viewDeclarations.v2[params.view];
    if (!v1View || !v2View)
        return false;
    const v2OnlyDims = Object.keys(v2View.dimensions).filter((k) => !(k in v1View.dimensions));
    const v2OnlyMeasures = Object.keys(v2View.measures).filter((k) => !(k in v1View.measures));
    return (params.dimensions.some((d) => v2OnlyDims.includes(d.field)) ||
        params.measures.some((m) => v2OnlyMeasures.includes(m.measure)) ||
        (params.filters ?? []).some((f) => v2OnlyDims.includes(f.column)));
}
/**
 * Returns the declared unit for a measure in a given view/version, or undefined
 * if the view or measure is not found. Use this to drive display formatting
 * (e.g. unit === "millisecond" → convert to seconds before rendering).
 */
function getMeasureUnit(viewName, measureName, version = "v1") {
    const versionViews = exports.viewDeclarations[version];
    const view = versionViews[viewName];
    return view?.measures[measureName]?.unit;
}
/**
 * Returns the unit of the value produced by `aggregation(measure)`. Falls back
 * to the measure's declared unit when the aggregation preserves it; returns
 * "integer" for `count` and `uniq` since those discard the source unit and
 * yield a dimensionless count.
 */
function getResultUnit(viewName, measureName, aggregation, version = "v1") {
    if (aggregation === "count" || aggregation === "uniq")
        return "integer";
    return getMeasureUnit(viewName, measureName, version);
}
const measureNamesSet = new Set();
for (const versionViews of Object.values(exports.viewDeclarations)) {
    for (const view of Object.values(versionViews)) {
        Object.keys(view.measures).forEach((m) => measureNamesSet.add(m));
    }
}
const measureNames = Array.from(measureNamesSet).sort();
exports.measures = zod_1.z.enum(measureNames);
